import { useState, useCallback, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const CONVERSATION_KEY = 'mcg_conversation_id';

export const useChat = (token, onLogout, webSearchEnabled = true) => {
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(() => localStorage.getItem(CONVERSATION_KEY));
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUserMessage, setLastUserMessage] = useState(null);

  // Ref so async streaming callbacks always see current conversationId
  const conversationIdRef = useRef(conversationId);
  useEffect(() => { conversationIdRef.current = conversationId; }, [conversationId]);

  // Ref so streamChat always reads the latest toggle value without re-creating the closure
  const webSearchEnabledRef = useRef(webSearchEnabled);
  useEffect(() => { webSearchEnabledRef.current = webSearchEnabled; }, [webSearchEnabled]);

  // ─── Fetch conversation list ──────────────────────────────────────────────
  const fetchConversations = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setConversations(data.conversations || []);
    } catch {
      // non-critical, silently ignore
    }
  }, [token]);

  // ─── On mount: restore conversation + load list ───────────────────────────
  useEffect(() => {
    if (!token) return;
    const storedId = localStorage.getItem(CONVERSATION_KEY);
    fetchConversations();
    if (!storedId) return;

    fetch(`${API_URL}/api/conversations/${storedId}/messages`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data) => {
        const loaded = data.messages.map((m) => ({
          id: m.timestamp,
          role: m.role,
          content: m.content,
        }));
        setMessages(loaded);
        setConversationId(storedId);
        conversationIdRef.current = storedId;
      })
      .catch(() => {
        localStorage.removeItem(CONVERSATION_KEY);
        setConversationId(null);
        conversationIdRef.current = null;
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ─── Core streaming function ──────────────────────────────────────────────
  const streamChat = useCallback(
    async (userMessage, isRegenerate = false) => {
      if (!userMessage.trim() || !token) return;

      const currentConvId = conversationIdRef.current;
      const isNewConversation = !currentConvId;
      const botPlaceholderId = Date.now();

      // Add user bubble (unless regenerating — user message is already shown)
      if (!isRegenerate) {
        setLastUserMessage(userMessage);
        setMessages((prev) => [
          ...prev,
          { id: botPlaceholderId - 1, role: 'user', content: userMessage },
        ]);
      }

      // Add streaming bot placeholder
      setMessages((prev) => [
        ...prev,
        { id: botPlaceholderId, role: 'assistant', content: '', streaming: true, toolCalls: [] },
      ]);

      setLoading(true);
      setError(null);

      try {
        const res = await fetch(`${API_URL}/api/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ message: userMessage, conversation_id: currentConvId, web_search_enabled: webSearchEnabledRef.current }),
        });

        if (!res.ok) {
          if (res.status === 401) { onLogout?.(); return; }
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `HTTP ${res.status}`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            let data;
            try { data = JSON.parse(line.slice(6)); } catch { continue; }

            if (data.type === 'chunk') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === botPlaceholderId ? { ...m, content: m.content + data.text } : m,
                ),
              );
            } else if (data.type === 'tool') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === botPlaceholderId
                    ? { ...m, toolCalls: [...(m.toolCalls || []), { name: data.name, actionGroup: data.actionGroup, query: data.query || '' }] }
                    : m,
                ),
              );
            } else if (data.type === 'done') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === botPlaceholderId ? { ...m, streaming: false } : m,
                ),
              );
              if (data.conversation_id) {
                localStorage.setItem(CONVERSATION_KEY, data.conversation_id);
                setConversationId(data.conversation_id);
                conversationIdRef.current = data.conversation_id;
              }
              if (isNewConversation && data.title) {
                // Title came back with the done event — update sidebar immediately
                setConversations((prev) =>
                  prev.map((c) =>
                    c.conversation_id === data.conversation_id ? { ...c, title: data.title } : c,
                  ),
                );
              }
              fetchConversations();
            } else if (data.type === 'error') {
              throw new Error(data.message || 'Unknown error from server');
            }
          }
        }
      } catch (err) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === botPlaceholderId ? { ...m, streaming: false, error: true } : m,
          ),
        );
        if (err.message !== 'Failed to fetch') {
          setError(err.message || 'Failed to get response');
        }
      } finally {
        setLoading(false);
      }
    },
    [token, onLogout, fetchConversations],
  );

  // ─── Public send + regenerate ─────────────────────────────────────────────
  const sendMessage = useCallback(
    (userMessage) => streamChat(userMessage, false),
    [streamChat],
  );

  const regenerateResponse = useCallback(() => {
    if (loading) return;
    // Use lastUserMessage if available, otherwise find the last user message in history
    const userMsg = lastUserMessage || (() => {
      const currentMessages = messages;
      for (let i = currentMessages.length - 1; i >= 0; i--) {
        if (currentMessages[i].role === 'user') return currentMessages[i].content;
      }
      return null;
    })();
    if (!userMsg) return;
    // Remove the last assistant message
    setMessages((prev) => {
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].role === 'assistant') return prev.slice(0, i);
      }
      return prev;
    });
    return streamChat(userMsg, true);
  }, [lastUserMessage, loading, messages, streamChat]);

  // ─── Conversation management ──────────────────────────────────────────────
  const loadConversation = useCallback(
    async (id) => {
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/conversations/${id}/messages`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const loaded = data.messages.map((m) => ({
          id: m.timestamp,
          role: m.role,
          content: m.content,
        }));
        setMessages(loaded);
        setConversationId(id);
        conversationIdRef.current = id;
        localStorage.setItem(CONVERSATION_KEY, id);
        setError(null);
        setLastUserMessage(null);
      } catch {
        setError('Failed to load conversation');
      }
    },
    [token],
  );

  const startNewConversation = useCallback(() => {
    localStorage.removeItem(CONVERSATION_KEY);
    setMessages([]);
    setConversationId(null);
    conversationIdRef.current = null;
    setError(null);
    setLastUserMessage(null);
  }, []);

  const searchConversations = useCallback(
    async (query) => {
      if (!token || !query || query.trim().length < 2) return [];
      try {
        const res = await fetch(
          `${API_URL}/api/conversations/search?q=${encodeURIComponent(query.trim())}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) return [];
        const data = await res.json();
        return data.matches || [];
      } catch {
        return [];
      }
    },
    [token],
  );

  const deleteConversation = useCallback(
    async (id) => {
      if (!token) return;
      try {
        await fetch(`${API_URL}/api/conversations/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (id === conversationIdRef.current) {
          localStorage.removeItem(CONVERSATION_KEY);
          setMessages([]);
          setConversationId(null);
          conversationIdRef.current = null;
        }
        await fetchConversations();
      } catch {
        setError('Failed to delete conversation');
      }
    },
    [token, fetchConversations],
  );

  return {
    messages,
    conversationId,
    conversations,
    loading,
    error,
    sendMessage,
    regenerateResponse,
    loadConversation,
    startNewConversation,
    deleteConversation,
    fetchConversations,
    searchConversations,
    lastUserMessage,
  };
};
